import React, { useCallback, useEffect, useState } from 'react';
import 'antd/dist/antd.css';
import './routes.css';
import { Navbar } from '../../components/Navbar';
import routeImage from '../../images/route.png';
import { RouteMap } from '../../components/routeMap/';
import BtnTheme from '../../components/buttonTheme/';
import { BsChevronDown } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { apiRoutesActions, routes } from '../../features/api_routes/apiRouterSlice';
import { apiNodePointsActions, nodePoints } from '../../features/api_node_points/apiNodePointsSlice';
import { FaArrowLeftLong } from 'react-icons/fa6';
import configService from '../../services/configService';
import { poiService } from '../../services/poiService';
import { CreatePoiPayload } from '../../types/types';

const AUTH_USERNAME = 'root';
const AUTH_PASSWORD = 'df4!338dAFJAssfK453A';

export const AdminLayout: React.FC = () => {
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [isCollapsed, setCollapsed] = useState<boolean>(false);
  const dispatch = useDispatch();
  const routesList = useSelector(routes);
  const [routesListSaved, setRoutesListSaved] = useState<Array<any>>([]);
  const [routeSelected, setRouteSelected] = useState<any>(null);
  const nodes = useSelector(nodePoints);
  const [nodeList, setNodePoints] = useState<Array<any>>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [moreInfo, setMoreInfo] = useState(false);
  const [poiFeedback, setPoiFeedback] = useState<string | null>(null);

  const handleAuthSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const normalizedUsername = username.trim().toLowerCase();
    const normalizedPassword = password.trim();
    const expectedUsername = AUTH_USERNAME.toLowerCase();
    const expectedPassword = AUTH_PASSWORD;

    const isValid = normalizedUsername === expectedUsername && normalizedPassword === expectedPassword;

    if (isValid) {
      setIsAuthorized(true);
      setUsername('');
      setPassword('');
      setAuthError(null);
    } else {
      setAuthError('Credenciales incorrectas, intenta nuevamente.');
    }
  }, [username, password]);

  const handleButtonClick = (route: any) => {
    setRouteSelected(route);
  };

  const handleMoreInfo = () => {
    setMoreInfo(!moreInfo);
  };

  const handleCloseMoreInfo = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setMoreInfo(false);
  };

  const handleCreatePoi = useCallback(async (payload: CreatePoiPayload) => {
    const tenantId = configService.getConfig().tenantId;

    const normalizedPayload: CreatePoiPayload = {
      title: payload.title,
      lat: payload.lat,
      lon: payload.lon,
      short_description: payload.short_description ?? null,
      image_url: payload.image_url ?? null,
    };

    try {
      const response = await poiService.createPoi(tenantId, normalizedPayload);
      setPoiFeedback(`POI "${response.data.title}" creado correctamente.`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo crear el POI';
      setPoiFeedback(message);
      throw error;
    }
  }, []);

  useEffect(() => {
    if (!isAuthorized || typeof window === 'undefined') {
      return;
    }

    const checkMobile = () => {
      const screenWidth = window.innerWidth <= 768;
      setIsMobile(screenWidth);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, [isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) {
      return;
    }

    if (!routesList) {
      dispatch(apiRoutesActions.fetchGetRoutes({}));
    }

    if (routesList) {
      const list = Array.isArray(routesList) ? [...routesList] : [];
      setRoutesListSaved(list);
      setRouteSelected(list.length > 0 ? list[0] : null);
    }
  }, [isAuthorized, routesList, dispatch]);

  useEffect(() => {
    if (!isAuthorized) {
      return;
    }

    if (!nodes) {
      dispatch(apiNodePointsActions.fetchGetNodePoints({}));
    }

    if (nodes) {
      const list = Array.isArray(nodes) ? [...nodes] : [];
      setNodePoints(list);
    }
  }, [isAuthorized, nodes, dispatch]);

  if (!isAuthorized) {
    return (
      <>
        <Navbar />
        <div className="admin-auth-overlay" role="dialog" aria-modal="true" aria-labelledby="admin-auth-title">
          <form className="admin-auth-modal" onSubmit={handleAuthSubmit}>
            <h2 id="admin-auth-title" className="admin-auth-title">Acceso administrador</h2>
            <p className="admin-auth-subtitle">Introduce tus credenciales para continuar.</p>

            <label className="admin-auth-field">
              <span>Usuario</span>
              <input
                className="admin-auth-input"
                type="text"
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  if (authError) {
                    setAuthError(null);
                  }
                }}
                autoComplete="username"
                autoFocus
              />
            </label>

            <label className="admin-auth-field">
              <span>Contraseña</span>
              <input
                className="admin-auth-input"
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  if (authError) {
                    setAuthError(null);
                  }
                }}
                autoComplete="current-password"
              />
            </label>

            {authError && <div className="admin-auth-error">{authError}</div>}

            <button type="submit" className="admin-auth-submit">Acceder</button>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />

      <div className="parent">
        <div className={`${isMobile ? 'bottom-sheet ' : 'left-sider'}`} style={{ height: isCollapsed ? '100px' : '' }}>
          <div className={`list-view-parent ${isMobile && moreInfo ? 'hidden' : ''}`}>
            <div className="title-container padded-left-16">
              <span className="title-text">Panel de administración</span>
              {isMobile && (
                <BsChevronDown
                  style={{
                    transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                    transitionDuration: '0.4s',
                    fontSize: 20,
                  }}
                  onClick={() => {
                    setCollapsed(!isCollapsed);
                  }}
                />
              )}
            </div>
            {poiFeedback && (
              <div className="create-poi-feedback padded-left-16">{poiFeedback}</div>
            )}

            <div className="flex-start-container padded-left-16 admin-stats">
              <span className="count-routes">{routesListSaved.length} rutas</span>
              <span className="count-routes">{nodeList.length} POIs</span>
            </div>

            <div className="list">
              {routesListSaved != null &&
                routesListSaved.map((route: any, index: number) => {
                  const isSelected =
                    routeSelected != null &&
                    routeSelected.id === route.id;

                  return (
                    <div
                      key={index}
                      className={`route-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleButtonClick(route)}
                    >
                      <div className="route-content">
                        <div className="route-info">
                          <img className="route-image" src={routeImage} alt="route" />
                          <div className="route-text">
                            <div className="nameText">{route.nombre}</div>
                          </div>
                        </div>
                        <BtnTheme text='+Info' onClick={handleMoreInfo} className={`${isSelected ? '' : 'hidden'}`}></BtnTheme>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
          {routeSelected != null && (
            <div className={`route-sheet ${moreInfo ? 'active' : ''}`}>
              <div className="scrollable-container">
                <div className="title-container ">
                  <div
                    style={{ paddingTop: '0px', display: 'inline-flex', justifyContent: 'space-between', width: '100%' }}
                    onClick={handleMoreInfo}
                  >
                    <FaArrowLeftLong color="#625B71" style={{ width: '20px', height: '20px', paddingTop: '3px', display: isMobile ? 'block' : 'none' }} />
                    {isMobile && (
                      <BsChevronDown
                        style={{
                          transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                          transitionDuration: '0.5s',
                          fontSize: 20,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCollapsed(!isCollapsed);
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="route-sheet-actions">
                  <BtnTheme text='Cerrar' onClick={handleCloseMoreInfo} />
                </div>
                <div className="results-section">
                  <span className="title-text">{routeSelected.nombre}</span>
                </div>
                <div className="description-route-container">
                  <span className="descriptionRoute">{routeSelected.descripción}</span>
                </div>
                <div className="results-section spacing-block">
                  <span className="distanceSpan"> Distancia: {routeSelected.distancia}</span>
                  <span className="distanceSpan"> Desnivel: {routeSelected.desnivel}</span>
                  <span className="distanceSpan"> Dificultad: {routeSelected.dificultad}</span>
                </div>

                <div className="gpx-download-container">
                  <button
                    className="buttonSelected"
                    onClick={() => {
                      if (routeSelected?.signed_url) {
                        const link = document.createElement('a');
                        link.href = routeSelected.signed_url;
                        link.download = `${routeSelected.nombre}.gpx`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                  >
                    Descargar GPX
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <RouteMap
          gpxUrl={routeSelected?.signed_url}
          isMobile={isMobile}
          isAdmin
          onCreatePoi={handleCreatePoi}
        />
      </div>
    </>
  );
};
